import React from 'react';

import {
  GenericBarChart,
  GenericBarChartProps,
} from 'app/components/Dashboard/GenericChart/GenericBarChart';
import {CategoricalStatResponse} from 'app/components/Dashboard/types';
import {getCategoricalData} from 'app/components/Dashboard/utils/categoricalDataUtils';
import {type ApiOrganizationAreaUnit} from 'app/modules/Remote/Organization';
import {MultifeatureAnalysisConfig} from 'app/src/components/Dashboard/types';
import * as C from 'app/utils/constants';
import * as layerUtils from 'app/utils/layerUtils';

// Only show the multi feature chart if there are less than 20 features,
// otherwise the chart will be too crowded and hard to read
const MAX_FEATURES_FOR_MULTI_FEATURE_CHART = 20;

export interface MultiFeatureAnalysisCategoricalChartProps {
  featureStatsResponse: CategoricalStatResponse;
  widgetConfig: MultifeatureAnalysisConfig;
  isExpanded: boolean;
  alwaysShowLegend?: boolean;
  onLegendClick?: GenericBarChartProps['onLegendClick'];
  hoveredFeatureId?: string | null;
  displayFeatureIds: Set<string>;
  currentCategoryId: string;
  dataViewUnit: 'area' | 'percentage';
  areaUnit: ApiOrganizationAreaUnit;
  allDates: string[];
}

export const MultiFeatureAnalysisCategoricalChart: React.FC<
  MultiFeatureAnalysisCategoricalChartProps
> = ({
  featureStatsResponse,
  widgetConfig,
  isExpanded,
  alwaysShowLegend = false,
  hoveredFeatureId,
  displayFeatureIds,
  currentCategoryId,
  dataViewUnit,
  areaUnit,
  onLegendClick,
  allDates,
}) => {
  const layer = layerUtils.getLayer(widgetConfig.layerKey);

  // This will only recompute when the actual data or layer changes
  const categoryData = React.useMemo(() => {
    if (!layer || layer.type !== 'image') {
      return null;
    }

    return getCategoricalData(
      featureStatsResponse,
      widgetConfig.layerKey,
      layer.layerLegendMap || {},
      widgetConfig.timeAggregation,
      areaUnit,
      allDates
    );
  }, [
    layer,
    featureStatsResponse,
    widgetConfig.layerKey,
    widgetConfig.timeAggregation,
    areaUnit,
    allDates,
  ]);

  const {chartData} = React.useMemo(() => {
    if (!categoryData) {
      return {chartData: []};
    }

    const {
      categoryChartData,
      singleCategoryChartDatas,
      categoryChartDataPercentages,
      singleCategoryChartDatasPercentages,
    } = categoryData;

    const dataOptions = {
      multiCategory: {
        area: categoryChartData,
        percentage: categoryChartDataPercentages,
      },
      singleCategory: {
        area: singleCategoryChartDatas[currentCategoryId],
        percentage: singleCategoryChartDatasPercentages[currentCategoryId],
      },
    };

    const categoryType = currentCategoryId === 'multiCategory' ? 'multiCategory' : 'singleCategory';
    const derivedDataset = dataOptions[categoryType][dataViewUnit];

    const hasManyFeatures =
      Object.keys(derivedDataset).length > MAX_FEATURES_FOR_MULTI_FEATURE_CHART;

    // This is which version of the data we're actually rendering
    const finalChartData = (() => {
      if (hoveredFeatureId) {
        return derivedDataset.filter((d) => d.id === hoveredFeatureId);
      }
      if (displayFeatureIds.size > 0) {
        return derivedDataset.filter((d) => displayFeatureIds.has(d.id));
      }
      if (hasManyFeatures) {
        return dataOptions.multiCategory[dataViewUnit].filter((d) => d.id === currentCategoryId);
      }
      return derivedDataset;
    })();

    return {
      chartData: finalChartData,
    };
  }, [categoryData, currentCategoryId, dataViewUnit, hoveredFeatureId, displayFeatureIds]);

  const unit = areaUnit === C.UNIT_AREA_ACRE ? 'acres' : 'hectares';
  const showPercentage = dataViewUnit === 'percentage';

  return (
    <GenericBarChart
      chartData={chartData}
      widgetConfig={widgetConfig}
      isExpanded={isExpanded}
      unit={unit}
      showPercentage={showPercentage}
      allDates={allDates}
      allowAnimation={false}
      alwaysShowLegend={alwaysShowLegend}
      onLegendClick={onLegendClick}
    />
  );
};
